/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: tabs/AudioTab.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Audio tab component for Conference form - Audio Settings
────────────────────────────────────────────────────────────────*/

import { Volume2 } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Toggle } from '../../../components/ui/Toggle';
import { FormBox } from '../../../components/ui/FormBox';
import type { ConferenceRoom } from '../types';

interface AudioTabProps {
  formData: ConferenceRoom;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (field: keyof ConferenceRoom, value: any) => void;
}

export function AudioTab({
  formData,
  onChange,
}: AudioTabProps) {
  const { t } = useI18n();
  const { isDebugModeActive } = useDebugMode();

  const buildTooltipContent = (tooltipKey: string, debugKey?: string) => {
    const base = t(tooltipKey);
    if (!debugKey || !isDebugModeActive) {
      return base;
    }
    return `${base}\n\n${t('conferences:debug.field')}: ${t(debugKey)}`;
  };

  return (
    <div className="space-y-3">
      <FormBox
        title={t('conferences:sections.audio')}
        icon={<Volume2 className="h-5 w-5" />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
          {/* Row 1 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.announceName', 'conferences:debug.announce_name')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.announceName')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.announceName}
              onChange={(value) => onChange('announceName', value)}
            />
          </div>
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.announceCount', 'conferences:debug.announce_count')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.announceCount')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.announceCount}
              onChange={(value) => onChange('announceCount', value)}
            />
          </div>

          {/* Row 2 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.enterSound', 'conferences:debug.enter_sound')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.enterSound')}
            </label>
          </SimpleTooltip>
          <input
            type="text"
            value={formData.enterSound}
            onChange={(e) => onChange('enterSound', e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder="tone_stream://%(200,0,1000)"
          />

          {/* Row 3 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.exitSound', 'conferences:debug.exit_sound')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.exitSound')}
            </label>
          </SimpleTooltip>
          <input
            type="text"
            value={formData.exitSound}
            onChange={(e) => onChange('exitSound', e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder="tone_stream://%(200,0,400)"
          />
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.energyLevel', 'conferences:debug.energy_level')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.energyLevel')}
            </label>
          </SimpleTooltip>
          <input
            type="number"
            value={formData.energyLevel}
            onChange={(e) => onChange('energyLevel', parseInt(e.target.value) || 300)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder="300"
            min="0"
            max="500"
          />

          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.callerControlsProfile', 'conferences:debug.caller_controls_profile')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.callerControlsProfile')}
            </label>
          </SimpleTooltip>
          <input
            type="text"
            value={formData.callerControlsProfile}
            onChange={(e) => onChange('callerControlsProfile', e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder="default"
          />

          {/* Row 4 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.conferenceFlags', 'conferences:debug.conference_flags')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.conferenceFlags')}
            </label>
          </SimpleTooltip>
          <input
            type="text"
            value={formData.conferenceFlags}
            onChange={(e) => onChange('conferenceFlags', e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder="mintwo,moderator"
          />
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.comfortNoise', 'conferences:debug.comfort_noise')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.comfortNoise')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.comfortNoise}
              onChange={(value) => onChange('comfortNoise', value)}
            />
          </div>

          {/* Row 5 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.autoGainControl', 'conferences:debug.auto_gain_control')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.autoGainControl')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.autoGainControl}
              onChange={(value) => onChange('autoGainControl', value)}
            />
          </div>
          <div></div>
          <div></div>
        </div>
      </FormBox>
    </div>
  );
}

